import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import styles from './SectionHeader.module.css';

export interface SectionHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode;
  /** Uppercase kicker above the title: the zone label. */
  eyebrow?: ReactNode;
  description?: ReactNode;
  /** Live catalogue count — the authoring palette shows one per primitive. */
  count?: number;
  actions?: ReactNode;
  /** Heading level. Pick the one the document outline needs, not a size. */
  level?: 2 | 3 | 4;
  size?: 'sm' | 'md';
  /** 2px top rule, for a band boundary. */
  ruled?: boolean;
}

export const SectionHeader = forwardRef<HTMLElement, SectionHeaderProps>(function SectionHeader(
  {
    title,
    eyebrow,
    description,
    count,
    actions,
    level = 2,
    size = 'md',
    ruled = false,
    className,
    ...rest
  },
  ref,
) {
  const Heading = `h${String(level)}` as 'h2' | 'h3' | 'h4';

  return (
    <header
      {...rest}
      ref={ref}
      className={clsx(styles.header, styles[size], ruled && styles.ruled, className)}
    >
      <div className={styles.text}>
        {eyebrow !== undefined && <p className={styles.eyebrow}>{eyebrow}</p>}
        <div className={styles.titleRow}>
          <Heading className={styles.title}>{title}</Heading>
          {count !== undefined && <span className={styles.count}>{count}</span>}
        </div>
        {description !== undefined && <p className={styles.description}>{description}</p>}
      </div>
      {actions !== undefined && <div className={styles.actions}>{actions}</div>}
    </header>
  );
});
